import React, { useState, useEffect } from 'react';
import {
  Table, Button, Card, Space, Modal, Typography, Tag,
  Row, Col, Input, Spin, Form, message, Popconfirm,
  Tooltip, Select, Divider, List, Avatar, QRCode
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  SettingOutlined, EnvironmentOutlined,
  UserOutlined, TeamOutlined, PictureOutlined, ShareAltOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import lbsContactWayService from '../services/lbsContactWayService';

const { Title, Text } = Typography;
const { Option } = Select;

const LbsContactWays = () => {
  const { token, tenant } = useAuth();
  const navigate = useNavigate();
  const [lbsContactWays, setLbsContactWays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [relationModalVisible, setRelationModalVisible] = useState(false);
  const [selectedLbs, setSelectedLbs] = useState(null);
  const [relations, setRelations] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareMiniLink, setShareMiniLink] = useState('');
  const [shareWebLink, setShareWebLink] = useState('');

  // 获取租户ID（统一实现）
  const getTenantId = () => {
    const tenantStr = localStorage.getItem('tenant');
    let tenantId = tenant?.id;
    try {
      if (tenantStr) {
        const tenantObj = JSON.parse(tenantStr);
        tenantId = tenantObj.id;
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
    }
    if (!tenantId) {
      tenantId = localStorage.getItem('tenantId');
    }
    return tenantId;
  };
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
  });

  const fetchLbsContactWays = async (page = 1, pageSize = 20, name = '') => {
    try {
      setLoading(true);
      const params = {
        page: page - 1,
        size: pageSize,
        ...(name && { name })
      };
      
      const response = await lbsContactWayService.getLbsContactWays(params);
      
      setLbsContactWays(response.data.content || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize,
        total: response.data.totalElements || 0
      }));
    } catch (error) {
      message.error('获取LBS活码列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (record) => {
    try {
      const tenantId = getTenantId();
      if (!tenantId) {
        message.warning('未找到租户信息，请重新登录');
      }
      const miniLink = `pages/index/index?tenantId=${tenantId || ''}&lbsId=${record.id}`;
      const base = window.location.origin || '';
      const webLink = `${base}/pages/lbs/index.html?tenantId=${tenantId || ''}&lbsId=${record.id}`;
      setShareMiniLink(miniLink);
      setShareWebLink(webLink);
      setShareModalVisible(true);
    } catch (e) {
      message.error('生成分享信息失败');
    }
  };

  useEffect(() => {
    fetchLbsContactWays();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    fetchLbsContactWays(1, pagination.pageSize, value);
  };

  const handleTableChange = (paginationConfig) => {
    fetchLbsContactWays(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  const handleDelete = async (id) => {
    try {
      await lbsContactWayService.deleteLbsContactWay(id);
      message.success('删除成功');
      fetchLbsContactWays(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('删除失败: ' + (error.message || '未知错误'));
    }
  };
  
  const handleEdit = (record) => {
    navigate(`/lbs-contact-ways/${record.id}/edit`);
  };

  const handleManageRelations = async (lbs) => {
    try {
      setSelectedLbs(lbs);
      setLoading(true);
      const response = await lbsContactWayService.getLbsContactWayRelations(lbs.id);
      setRelations(response.data || []);
      setRelationModalVisible(true);
    } catch (error) {
      message.error('获取关联信息失败');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteRelation = async (relationId) => {
    try {
      await lbsContactWayService.deleteLbsContactWayRelation(relationId);
      message.success('删除关联成功');
      
      const response = await lbsContactWayService.getLbsContactWayRelations(selectedLbs.id);
      setRelations(response.data || []);
    } catch (error) {
      message.error('删除关联失败');
    }
  };

  const columns = [
    {
      title: 'LBS活码名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: '类型',
      key: 'type',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const relations = record.relations || [];
        const memberCount = relations.filter(r => r.contactWayType === 'MEMBER_CONTACT_WAY').length;
        const groupCount = relations.filter(r => r.contactWayType !== 'MEMBER_CONTACT_WAY').length;
        
        if (memberCount > 0 && groupCount > 0) {
          return <span>混合</span>;
        } else if (memberCount > 0) {
          return <span>成员</span>;
        } else if (groupCount > 0) {
          return <span>社群</span>;
        } else {
          return <span>-</span>;
        }
      }
    },
    {
      title: '数量',
      key: 'count',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const relations = record.relations || [];
        return <span>{relations.length}</span>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        const color = status === 'active' ? 'success' : 'default';
        const text = status === 'active' ? '启用' : '禁用';
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<ShareAltOutlined />}
            onClick={() => handleShare(record)}
          >
            分享
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个LBS活码吗？"
            description="删除后将无法恢复，且会删除相关的关联关系。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const getContactWayTypeIcon = (type) => {
    return type === 'MEMBER_CONTACT_WAY' ? <UserOutlined /> : <TeamOutlined />;
  };

  const getContactWayTypeName = (type) => {
    return type === 'MEMBER_CONTACT_WAY' ? '成员活码' : '社群活码';
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>LBS活码管理</Title>
          <Text type="secondary">
            管理所有LBS活码和关联的成员/群活码，支持批量创建和地理位置关联。
          </Text>
        </Col>
      </Row>
      
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索LBS活码名称"
                  enterButton
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  style={{ width: 200 }}
                  allowClear
                />
              </Space>
            </Col>
          </Row>
          
          <Row>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/lbs-contact-ways/batch')}
                >
                  创建LBS活码
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
        
        <Table
          columns={columns}
          dataSource={lbsContactWays}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </Card>

      {/* 分享模态框：合并小程序链接与网页二维码 */}
      <Modal
        title="分享"
        open={shareModalVisible}
        onCancel={() => setShareModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShareModalVisible(false)}>关闭</Button>
        ]}
        width={680}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Card size="small" title="小程序链接">
              <Text type="secondary">点击复制链接：</Text>
              <div style={{ marginTop: 8 }}>
                <Typography.Paragraph copyable={{ text: shareMiniLink }} style={{ marginBottom: 0 }}>
                  {shareMiniLink}
                </Typography.Paragraph>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" title="网页二维码" style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <QRCode value={shareWebLink || '-'} size={180} />
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">链接：</Text>
                  <Typography.Paragraph copyable={{ text: shareWebLink }} style={{ marginBottom: 0, wordBreak: 'break-all', textAlign: 'center' }}>
                    {shareWebLink}
                  </Typography.Paragraph>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default LbsContactWays;